import { ConfigTools } from '../view/ConfigTools.js';
import { UiCommandRegistry } from './imcmd/UiCommandRegistry.js';
import { DomReaper } from './DomReaper.js';
import { Validation, setWidgetEnumerator } from './Validation.js';
import { WidgetRegistry } from './WidgetRegistry.js';
import { ServerCmdProviderRegistry } from './cmd/ServerCmdProviderRegistry.js';
import { mountWidgets } from './WidgetsMount.js';

// core/Widget.ts
/**
 * Базовый класс для создания виджетов.
 *
 * Важно! - при вызове конструктора напрямую
 * нужно после создания класса использовать метод init().
 * Метод getOrInit реализует эту логику внутри.
 */
class Widget {
    /**
     * @param el        DOM-элемент, к которому будет привязан виджет.
     *                  Обязателен, иначе будет выброшена ошибка.
     * @param options   Начальные параметры инициализации виджета.
     *                  Сохраняются до вызова init(), чтобы затем быть переданными в _create().
     * @param skipInit  Если false (по умолчанию) — вызовет init() автоматически через microtask.
     *                  При ручном использовании new MyWidget(...) это сработает, но может вызвать
     *                  побочные эффекты из-за отложенной инициализации — будет выведено предупреждение.
     *                  Для полной синхронной готовности виджета установите skipInit = true и вызовите init() вручную.
     */
    constructor(el, options, skipInit) {
        this._initialized = false;
        this._destroyed = false;
        if (!el) {
            throw new Error('Widget requires a host element');
        }
        this.element = el;
        const ctor = this.constructor;
        if (!ctor.instances)
            ctor.instances = new WeakMap();
        // Регистрируем локально (per‑class) и глобально (общий наблюдатель)
        ctor.instances.set(this.element, this);
        // Регистрируем глобально
        const bag = Widget._all.get(this.element) ?? new Map();
        const selfCtor = this.constructor;
        if (bag.has(selfCtor)) {
            // жёсткое правило: один виджет данного типа на элемент
            throw new Error(`[Widget] На элемент уже привязан виджет этого типа: ${selfCtor instanceof Function ? selfCtor.name : 'Unknown'}`);
        }
        bag.set(selfCtor, this);
        Widget._all.set(this.element, bag);
        Widget._live.add(this);
        // глобально (общий наблюдатель)
        DomReaper.add(this.element, this);
        this._init_options = options ?? {};
        // Автоинициализация, если не указано, что init будет вызван вручную
        if (!skipInit) {
            console.warn(`[Widget] Автоинициализация из конструктора (${ctor.name}) не рекомендуется. 
               Лучше использовать getOrInit() или явный вызов init().`);
            console.trace(ctor.name);
            queueMicrotask(() => this.init());
        }
    }
    /** Идемпотентная инициализация */
    init() {
        if (this._initialized)
            return;
        this._initialized = true;
        // Инициализация создания
        try {
            this._create(this._init_options);
        }
        catch (ex) {
            // ⚠️ Важно: при провале инициализации — полный cleanup,
            // чтобы затем можно было создать заново
            console.error(`[Widget] Ошибка при инициализации`);
            // Снимаем ТОЛЬКО себя из DomReaper и вычищаем собственные реестры
            try {
                DomReaper.remove(this.element, this);
            }
            catch { }
            try {
                this.__reaperDestroy();
            }
            catch { }
            throw ex;
        }
        finally {
            this._init_options = undefined;
        }
        // Контроль имени виджета и контроль наличия класса стиля
        try {
            this._ensureDataWidgetAttr();
            this._ensureStyleVariant();
        }
        catch (ex) {
            console.error(ex);
        }
    }
    /**
     * Возвращает экземпляр виджета по значению атрибута `data-key`.
     * Ищет первый элемент в документе, соответствующий ключу.
     *
     * Если задан `elementCtor`, проверяет DOM-тип элемента и вызывает ошибку при несоответствии.
     * Требуется уникальность `data-key` в пределах документа.
     *
     * ℹ️ Если экземпляр уже существует, `options` игнорируются и возвращается он же.
     *
     * @param dataKey Значение атрибута `data-key` для поиска.
     * @param container Необязательный контейнер для поиска
     * @param options Параметры инициализации (учитываются только при первом создании).
     * @returns Экземпляр виджета или `undefined`, если элемент не найден.
     */
    //TODO: Сделать тесты
    static findByDataKey(dataKey, container, options) {
        const root = container ?? document;
        const el = root.querySelector(`[data-key="${dataKey}"]`);
        if (!el) {
            return undefined; // не нашли — возвращаем undefined
        }
        // Если подкласс указал ожидаемый DOM‑тип — проверим
        const ExpectedElCtor = this.elementCtor;
        if (ExpectedElCtor && !(el instanceof ExpectedElCtor)) {
            throw new Error(`Элемент с data-key="${dataKey}" не соответствует ожидаемому DOM‑типу (получен ${el.constructor?.name ?? 'Unknown'}).`);
        }
        return this.getOrInit(el, options);
    }
    /**
     * Единая точка входа: вернёт существующий или создаст + инициализирует новый экземпляр.
     * Возвращает существующий экземпляр виджета для элемента или создаёт новый.
     * Если экземпляр уже существует, переданные `options` игнорируются.
     */
    static getOrInit(element, options) {
        if (!(element instanceof HTMLElement)) {
            console.error("element is not HTMLElement", element);
            throw new Error("element is not HTMLElement");
        }
        const map = this.instances;
        const exist = map?.get(element);
        if (exist)
            return exist;
        // Если опции не передали — соберём их из DOM
        let finalOptions = options;
        if (finalOptions === undefined) {
            // 1) пробуем резолвер у самого класса
            let resolver;
            try {
                resolver = this._propResolver?.();
            }
            catch { /* ignore */ }
            // 2) если у класса нет — попробуем взять из реестра по ctor
            if (!resolver) {
                try {
                    const def = WidgetRegistry.getByCtor(this);
                    resolver = def?.propResolver;
                }
                catch (e) {
                    console.error(e);
                }
            }
            if (resolver) {
                try {
                    finalOptions = resolver(element);
                }
                catch { /* fallback ниже */ }
            }
            // 3) если резолвера нет или он упал — парсим data-options / dataset
            if (finalOptions === undefined) {
                // пробуем получить схему опций
                let scheme;
                try {
                    scheme = this._optionScheme?.();
                }
                catch { /* ignore */ }
                finalOptions = ConfigTools.parseDomOptions(element, scheme);
            }
        }
        const inst = new this(element, finalOptions, true);
        inst.init();
        Validation.init(element);
        return inst;
    }
    /**
     * Возвращает экземпляр виджета, связанный с указанным элементом, либо `undefined`, если не найден.
     */
    static from(element) {
        const map = this.instances;
        return map?.get(element);
    }
    /**
     * Вернуть виджет указанного типа, если он привязан к элементу
     */
    static getOn(el, type) {
        const bag = Widget._all.get(el);
        return bag?.get(type) ?? undefined;
    }
    /**
     * Вернуть ВСЕ виджеты на элементе (разных типов)
     */
    static allOn(el) {
        const bag = Widget._all.get(el);
        return bag ? Array.from(bag.values()) : [];
    }
    static getAll(ctor) {
        const arr = Array.from(Widget._live);
        return ctor ? arr.filter(w => w instanceof ctor) : arr;
    }
    /**
     * Регистрирует виджет в реестре.
     */
    static register() {
        // Регистрация виджета
        const ctor = this; // только для передачи в реестр
        WidgetRegistry.register(ctor, this._cssKey(), this._propResolver(), this._optionScheme(), this._alias);
        this.registerCommandProviders();
    }
    /**
     * Регистрирует командные провайдеры виджета.
     * Вызов идет при прямой регистрации и при ленивой регистрации виджета
     * см. ctorAny.registerCommandProviders();
     */
    static registerCommandProviders() {
        try {
            // UI-команды
            const uiProv = this._uiCommandProviders;
            const uiProvided = typeof uiProv === 'function'
                ? uiProv.call(this)
                : uiProv;
            if (uiProvided) {
                const list = Array.isArray(uiProvided) ? uiProvided : [uiProvided];
                for (const p of list) {
                    if (p) {
                        UiCommandRegistry.uiCommands.registerProvider(p);
                    }
                }
            }
            // Server-команды
            const srvProv = this._serverCommandProviders;
            const srvProvided = typeof srvProv === 'function'
                ? srvProv.call(this)
                : srvProv;
            if (srvProvided) {
                const list = Array.isArray(srvProvided) ? srvProvided : [srvProvided];
                for (const p of list) {
                    if (p) {
                        ServerCmdProviderRegistry.serverCommands.registerProvider(p);
                    }
                }
            }
        }
        catch (e) {
            console.error(e);
        }
    }
    static closestOnByName(el, name, cssKey) {
        if (!el || !name)
            return undefined;
        let cssMask;
        if (cssKey)
            cssMask = `, .${cssKey}`;
        else
            cssMask = '';
        const mask = `[data-widget~="${name}"]${cssMask}`;
        // сначала проверим сам элемент
        if (el.matches(mask)) {
            // если getOnByName тоже с дженериками — используем их
            return this.getOnByName(el, name);
        }
        // потом closest по дереву (self уже проверили выше)
        const host = el.closest(mask);
        if (!host)
            return undefined;
        let result = this.getOnByName(host, name);
        // Пока добавляю страховку только для view 27-12-2025, причина - виджет обращался ко view до ее инициализации
        if (!result && name === 'View') {
            // На случай непоследовательного монтирования виджета view
            mountWidgets(host);
            result = this.getOnByName(host, name);
        }
        return result;
    }
    static getOnByName(el, name) {
        if (!el)
            return undefined;
        if (!name)
            return undefined;
        const list = Widget.allOn(el);
        if (!list.length)
            return undefined;
        for (const w of list) {
            const ctor = w.constructor;
            // 1) основное: берём имя из реестра
            let defName;
            try {
                defName = WidgetRegistry.getByCtor?.(ctor)?.name;
            }
            catch { /* ignore */ }
            // 2) запасные варианты на случай отсутствия записи в реестре
            const alias = ctor._alias;
            if (name === defName || name === alias) {
                return w;
            }
        }
        return undefined;
    }
    /** Класс css для распознавания виджета */
    static _cssKey() {
        return undefined;
    }
    /** Кастомный резолвер опций из DOM (настраивается в наследнике). */
    static _propResolver() {
        return undefined;
    }
    /** Опциональная схема парсинга для этого виджета */
    static _optionScheme() {
        return undefined;
    }
    /** Корректное полное уничтожение виджета */
    destroy() {
        if (this._destroyed)
            return;
        try {
            // Пробуем вызвать через reaper
            DomReaper.remove(this.element, this);
        }
        catch (e) {
            console.error(e);
        }
        try {
            // Страхуем прямым вызовом
            this.__reaperDestroy();
        }
        catch (e) {
            console.error(e);
        }
    }
    /** @internal
     * Вызывается DomReaper при удалении узла из DOM. Ручной вызов не требуется.
     * Удаление DomReaper.remove выполняет сам DomReaper.remove
     */
    __reaperDestroy() {
        if (this._destroyed)
            return;
        this._destroyed = true;
        const ctor = this.constructor;
        Validation.unwire(this.element);
        this._onDestroy();
        ctor.instances?.delete(this.element);
        // Удаляем и из глобальной карты
        const bag = Widget._all?.get(this.element);
        if (bag) {
            bag.delete(this.constructor);
            if (bag.size === 0) {
                Widget._all.delete(this.element);
            }
        }
        Widget._live.delete(this);
    }
    // TODO: Подумать, нужно ли подключить ее + как с константным стилем тогда?
    /** Убедиться, что data-widget содержит имя текущего виджета; при отсутствии — добавить (через dataset). */
    _ensureDataWidgetAttr() {
        const el = this.element;
        const ctor = this.constructor;
        // имя виджета: реестр → alias → имя класса без "Widget"
        const reg = WidgetRegistry.getByCtor?.(ctor);
        const expected = reg?.name
            ?? ctor._alias;
        if (!expected)
            return;
        const current = el.dataset.widget?.trim() ?? "";
        if (current === "") {
            el.dataset.widget = expected; // data-widget="<expected>"
            return;
        }
        const parts = current.split(/\s+/).filter(Boolean);
        if (!parts.includes(expected)) {
            parts.push(expected);
            el.dataset.widget = parts.join(" "); // data-widget="... <expected>"
        }
    }
    /** Если задан data-style, то добавляет его в стили элемента */
    _ensureStyleVariant() {
        const variantRaw = this.element.dataset.style?.trim();
        if (!variantRaw)
            return;
        if (!this.element.classList.contains(variantRaw)) {
            this.element.classList.add(variantRaw);
        }
    }
    /**
     * Отправляет кастомное событие изменения значения, инициированное пользователем.
     * Используется для уведомления внешних обработчиков, что значение изменилось вручную.
     */
    _dispatchUserChange() {
        const ev = new CustomEvent('change', {
            bubbles: true,
            detail: { isCustom: true }
        });
        this.element.dispatchEvent(ev);
    }
}
/** Глобальная карта */
Widget._all = new WeakMap();
/** Глобальный список живых виджетов (для перечисления) */
Widget._live = new Set();
// Подключаем перечислитель виджетов для Validation
setWidgetEnumerator((el) => Widget.allOn(el));

export { Widget };
